import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import { submitPrivateInsuranceForm, clearPrivateInsuranceData } from "../features/privateInsurance/privateInsurance.slice";
import '../scss/private-insurance-page-new.scss';
import { privateInsuranceImage } from './images/jpg/privateinsuranceimage';

// Image constants from Figma - Exact URLs from design

interface PrivateInsuranceFormData {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
}

const PrivateInsurancePage: React.FC = () => {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const dispatch = useAppDispatch();
  const privateInsuranceState = useAppSelector((state) => state?.privateInsurance);
  const { privateInsuranceData, loading, error } = privateInsuranceState || { privateInsuranceData: null, loading: false, error: null };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PrivateInsuranceFormData>();

  const onSubmit = useCallback(async (data: PrivateInsuranceFormData) => {
    // Test toast immediately
    toast.info('Form submission started...');
    
    try {
      console.log('Form submitted with data:', data);
      
      const privateInsurancePayload = {
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        phoneNumber: '', // Not required in the form but API expects it
        message: `Private Insurance Quote Request - Address: ${data.address}`,
        subject: "Private Insurance Quote Request",
        firstName: data.firstName,
        lastName: data.lastName,
        address: data.address,
      };

      console.log('Dispatching with payload:', privateInsurancePayload);
      
      const result = await dispatch(submitPrivateInsuranceForm(privateInsurancePayload));
      
      console.log('Result from dispatch:', result);
      
      if (submitPrivateInsuranceForm.fulfilled.match(result)) {
        console.log('Success - showing toast');
        setShowSuccess(true);
        reset();
        toast.success(t("MESSAGE_SENT_SUCCESSFULLY") as string);

        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      } else if (submitPrivateInsuranceForm.rejected.match(result)) {
        console.log('Rejected - showing error toast:', result.payload || result.error);
        const errorMessage = result.payload || result.error?.message || t("MESSAGE_SEND_ERROR");
        toast.error(errorMessage as string);
      } else {
        console.log('Unknown result - showing generic error toast');
        toast.error(t("MESSAGE_SEND_ERROR") as string);
      }
    } catch (error) {
      console.error('Catch error:', error);
      toast.error(t("MESSAGE_SEND_ERROR") as string);
    }
  }, [dispatch, reset, t]);

  return (
    <div className="private-insurance-page">

      {/* Main Content Section */}
      <section className="main-content px-0">
        <Container>
          <div className="content-wrapper">
            <div className="text-content">
               <div className="title-section">
                <h2 className="main-title">
                  {t("PRIVATE_INSURANCE_MAIN_TITLE")}
                </h2>
              </div>
              <div className="description-section">
                <p className="main-description">
                  {t("PRIVATE_INSURANCE_MAIN_DESCRIPTION")}
                </p>
                <a 
                  href="https://www.api.invozio.com/img/F%C3%B6rs%C3%A4kringsbrev.pdf?_gl=1*1n1guna*_gcl_au*MTU4ODAwMTk2NS4xNzU0MDI2NDUy*_ga*MTQyNTE0OTA3Mi4xNzU0MDI2NDUy*_ga_DJKEC77RNR*czE3NTU1MDc3NjkkbzI0JGcxJHQxNzU1NTA4MDk3JGo2MCRsMCRoMA.."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button"
                >
                  {t("PRIVATE_INSURANCE_INFO_LINK")}
                  <div className="arrow-icon"></div>
                </a>
              </div>
             
            </div>
          </div>
        </Container>
      </section>
      
      {/* Hero Image Section */}
      <section className="hero-image-section">
        <Container>
          <div className="private-insurance-hero-image">
            <img
              src={privateInsuranceImage}
              alt={t("PRIVATE_INSURANCE")}
              loading="lazy"
              className="private-insurance-main-image"
            />
          </div>
        </Container>
      </section>

      {/* Quote and Contact Section */}
      <section className="quote-contact-section px-0">
        <Container>
          <div className="section-wrapper">
            {/* Request Quote Form */}
            <div className="quote-form-container mb-5">
              <div className="quote-form">
                <h3 className="form-title">{t("PRIVATE_INSURANCE_FORM_TITLE")}</h3>
                <p className="form-description">
                  {t("PRIVATE_INSURANCE_FORM_DESCRIPTION")}
                </p>

                <form className="quote-form-fields" onSubmit={handleSubmit(onSubmit)}>
                  <div className="input-field">
                    <label htmlFor="firstName">{t("PRIVATE_INSURANCE_FORM_FIRST_NAME")}</label>
                    <input
                      type="text"
                      id="firstName"
                      className={`form-input ${errors.firstName ? "error" : ""}`}
                      placeholder={t("PRIVATE_INSURANCE_FORM_FIRST_NAME_PLACEHOLDER")}
                      {...register("firstName", {
                        required: t("PRIVATE_INSURANCE_FORM_FIRST_NAME_REQUIRED"),
                        minLength: { value: 2, message: t("PRIVATE_INSURANCE_FORM_FIRST_NAME_MIN_LENGTH") },
                      })}
                    />
                    {errors.firstName && <span className="error-message">{errors.firstName.message}</span>}
                  </div>

                  <div className="input-field">
                    <label htmlFor="lastName">{t("PRIVATE_INSURANCE_FORM_LAST_NAME")}</label>
                    <input
                      type="text"
                      id="lastName"
                      className={`form-input ${errors.lastName ? "error" : ""}`}
                      placeholder={t("PRIVATE_INSURANCE_FORM_LAST_NAME_PLACEHOLDER")}
                      {...register("lastName", {
                        required: t("PRIVATE_INSURANCE_FORM_LAST_NAME_REQUIRED"),
                        minLength: { value: 2, message: t("PRIVATE_INSURANCE_FORM_LAST_NAME_MIN_LENGTH") },
                      })}
                    />
                    {errors.lastName && <span className="error-message">{errors.lastName.message}</span>}
                  </div>

                  <div className="input-field">
                    <label htmlFor="email">{t("PRIVATE_INSURANCE_FORM_EMAIL")}</label>
                    <input
                      type="email"
                      id="email"
                      className={`form-input ${errors.email ? "error" : ""}`}
                      placeholder={t("PRIVATE_INSURANCE_FORM_EMAIL_PLACEHOLDER")}
                      {...register("email", {
                        required: t("PRIVATE_INSURANCE_FORM_EMAIL_REQUIRED"),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t("PRIVATE_INSURANCE_FORM_EMAIL_INVALID"),
                        },
                      })}
                    />
                    {errors.email && <span className="error-message">{errors.email.message}</span>}
                  </div>

                  <div className="input-field">
                    <label htmlFor="address">{t("PRIVATE_INSURANCE_FORM_ADDRESS")}</label>
                    <input
                      type="text"
                      id="address"
                      className={`form-input ${errors.address ? "error" : ""}`}
                      placeholder={t("PRIVATE_INSURANCE_FORM_ADDRESS_PLACEHOLDER")}
                      {...register("address", {
                        required: t("PRIVATE_INSURANCE_FORM_ADDRESS_REQUIRED"),
                        minLength: { value: 5, message: t("PRIVATE_INSURANCE_FORM_ADDRESS_MIN_LENGTH") },
                      })}
                    />
                    {errors.address && <span className="error-message">{errors.address.message}</span>}
                  </div>

                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? t("PRIVATE_INSURANCE_FORM_SENDING") : t("PRIVATE_INSURANCE_FORM_SUBMIT")}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="contact-info">
              <h3 className="contact-title-private">{t("PRIVATE_INSURANCE_CONTACT_TITLE")}</h3>
              <p className="contact-description">
                {t("PRIVATE_INSURANCE_CONTACT_DESCRIPTION")}
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#6750B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="#6750B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="contact-content">
                    <h4>{t("PRIVATE_INSURANCE_CONTACT_SPECIALIST_TITLE")}</h4>
                    <p>{t("PRIVATE_INSURANCE_CONTACT_SPECIALIST_VALUE")}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="#6750B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="contact-content">
                    <h4>{t("PRIVATE_INSURANCE_CONTACT_PHONE_TITLE")}</h4>
                    <p>{t("PRIVATE_INSURANCE_CONTACT_PHONE_VALUE")}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#6750B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="22,6 12,13 2,6" stroke="#6750B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="contact-content">
                    <h4>{t("PRIVATE_INSURANCE_CONTACT_EMAIL_TITLE")}</h4>
                    <p>{t("PRIVATE_INSURANCE_CONTACT_EMAIL_VALUE")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>   
    </div>
  );
};

export default PrivateInsurancePage;
